import { useState } from 'react';
import type { Match } from '../../types';
import { Card, CardHeader } from '../ui/Card';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import {
  Table, TableHead, TableBody, TableRow, TableHeader, TableCell,
} from '../ui/Table';
import { formatDate } from '../../utils/helpers';
import { Input } from '../ui/Input';
import { ArrowRight, Inbox, Bot, Calendar, X } from 'lucide-react';

interface MatchesTableProps {
  matches: Match[];
  showAll?: boolean;
  /** Renders a From/To date filter in the header when provided. */
  dateFilter?: {
    from: string;
    to: string;
    onFromChange: (value: string) => void;
    onToChange: (value: string) => void;
    onClear: () => void;
    loading?: boolean;
  };
}

function MatchDateFilter({ from, to, onFromChange, onToChange, onClear, loading }: NonNullable<MatchesTableProps['dateFilter']>) {
  return (
    <div className="flex flex-wrap items-end gap-3 mb-4">
      <div className="flex items-center gap-2 text-xs font-semibold text-[#94a3b8] uppercase tracking-wider shrink-0 pb-2.5">
        <Calendar className="w-4 h-4" />
        <span>Matched</span>
      </div>
      <div className="w-[170px]">
        <Input
          type="date"
          label="From"
          value={from}
          max={to || undefined}
          onChange={(e) => onFromChange(e.target.value)}
          className="[color-scheme:dark]"
        />
      </div>
      <div className="w-[170px]">
        <Input
          type="date"
          label="To"
          value={to}
          min={from || undefined}
          onChange={(e) => onToChange(e.target.value)}
          className="[color-scheme:dark]"
        />
      </div>
      {(from || to) && (
        <Button variant="ghost" size="sm" onClick={onClear} icon={<X className="w-4 h-4" />}>
          Clear
        </Button>
      )}
      {loading && (
        <div className="pb-2.5">
          <div className="animate-spin w-4 h-4 border-2 border-[#3b82f6] border-t-transparent rounded-full" />
        </div>
      )}
    </div>
  );
}

function MatchSummaryModal({ match, onClose }: { match: Match; onClose: () => void }) {
  const pct = match.matchPercentage;
  const pctColor = pct >= 80 ? 'text-[#22c55e]' : pct >= 60 ? 'text-[#3b82f6]' : 'text-[#f59e0b]';

  return (
    <Modal isOpen onClose={onClose} title="Match Details" size="md"
      footer={<Button variant="secondary" onClick={onClose}>Close</Button>}
    >
      {/* Users + twins */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex-1 bg-[#1c1c2a] rounded-xl p-4 text-center">
          <div className="w-10 h-10 gradient-blue rounded-xl flex items-center justify-center text-sm font-bold text-white mx-auto mb-2">
            {match.user1.name.charAt(0).toUpperCase()}
          </div>
          <p className="text-sm font-semibold text-[#f1f5f9]">{match.user1.name}</p>
          <div className="flex items-center justify-center gap-1 mt-1 text-xs text-[#64748b]">
            <Bot className="w-3 h-3" />
            {match.user1.twinName}
          </div>
        </div>

        <div className="flex flex-col items-center gap-1 shrink-0">
          <p className={`text-3xl font-bold tabular-nums ${pctColor}`}>{pct}%</p>
          <ArrowRight className="w-4 h-4 text-[#64748b]" />
          <p className="text-xs text-[#64748b]">match</p>
        </div>

        <div className="flex-1 bg-[#1c1c2a] rounded-xl p-4 text-center">
          <div className="w-10 h-10 gradient-purple rounded-xl flex items-center justify-center text-sm font-bold text-white mx-auto mb-2">
            {match.user2.name.charAt(0).toUpperCase()}
          </div>
          <p className="text-sm font-semibold text-[#f1f5f9]">{match.user2.name}</p>
          <div className="flex items-center justify-center gap-1 mt-1 text-xs text-[#64748b]">
            <Bot className="w-3 h-3" />
            {match.user2.twinName}
          </div>
        </div>
      </div>

      {/* Match percentage bar */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">Match Strength</p>
          <span className={`text-xs font-semibold ${pctColor}`}>{pct}%</span>
        </div>
        <div className="h-2 bg-[#252535] rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${pct}%`,
              backgroundColor: pct >= 80 ? '#22c55e' : pct >= 60 ? '#3b82f6' : '#f59e0b',
            }}
          />
        </div>
      </div>

      {/* Summary */}
      <div className="mt-4">
        <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-2">AI Summary</p>
        <div className="bg-[#0f0f1a] border border-[#252535] rounded-xl p-4 max-h-48 overflow-y-auto">
          {match.summary ? (
            <p className="text-sm text-[#94a3b8] leading-relaxed whitespace-pre-line">{match.summary}</p>
          ) : (
            <em className="text-sm text-[#64748b]">No summary available for this match.</em>
          )}
        </div>
      </div>

      <p className="mt-4 text-xs text-[#64748b] text-right">Matched on {formatDate(match.createdAt)}</p>
    </Modal>
  );
}

export function MatchesTable({ matches, showAll = false, dateFilter }: MatchesTableProps) {
  const filtered = Boolean(dateFilter && (dateFilter.from || dateFilter.to));
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  const displayMatches = showAll ? matches : matches.slice(0, 5);

  const getMatchBadge = (percentage: number) => {
    if (percentage >= 80) return <Badge variant="success">{percentage}%</Badge>;
    if (percentage >= 60) return <Badge variant="info">{percentage}%</Badge>;
    return <Badge variant="warning">{percentage}%</Badge>;
  };

  return (
    <>
      <Card>
        <CardHeader
          title="Recent Matches"
          subtitle="Click any row to view match details"
          action={
            <span className="text-xs text-[#64748b] bg-[#1c1c2a] px-2.5 py-1 rounded-lg border border-[#252535]">
              {displayMatches.length} matches
            </span>
          }
        />

        {dateFilter && <MatchDateFilter {...dateFilter} />}

        {displayMatches.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-12 h-12 bg-[#1c1c2a] rounded-2xl flex items-center justify-center mb-3">
              <Inbox className="w-5 h-5 text-[#64748b]" />
            </div>
            <p className="text-sm font-medium text-[#94a3b8]">
              {filtered ? 'No matches in this date range' : 'No matches yet'}
            </p>
            <p className="text-xs text-[#64748b] mt-1">
              {filtered
                ? 'Try widening the dates or clear the filter'
                : 'Matches will appear here once users start connecting'}
            </p>
          </div>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>User 1</TableHeader>
                <TableHeader>Twin</TableHeader>
                <TableHeader>&nbsp;</TableHeader>
                <TableHeader>User 2</TableHeader>
                <TableHeader>Twin</TableHeader>
                <TableHeader>Match</TableHeader>
                <TableHeader>Date</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayMatches.map((match) => (
                <TableRow key={match.matchId} hover onClick={() => setSelectedMatch(match)}>
                  <TableCell>
                    <span className="font-medium text-[#f1f5f9]">{match.user1.name}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-[#64748b] text-sm">{match.user1.twinName}</span>
                  </TableCell>
                  <TableCell>
                    <div className="w-6 h-6 rounded-full bg-[#252535] flex items-center justify-center">
                      <ArrowRight className="w-3 h-3 text-[#64748b]" />
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-medium text-[#f1f5f9]">{match.user2.name}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-[#64748b] text-sm">{match.user2.twinName}</span>
                  </TableCell>
                  <TableCell>{getMatchBadge(match.matchPercentage)}</TableCell>
                  <TableCell className="text-[#64748b] text-sm">{formatDate(match.createdAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {selectedMatch && (
        <MatchSummaryModal match={selectedMatch} onClose={() => setSelectedMatch(null)} />
      )}
    </>
  );
}
